import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import API_BASE_URL from "../config";
import {
  FiCalendar,
  FiBriefcase,
  FiFileText,
  FiSettings,
  FiLogOut,
  FiMenu,
  FiX,
  FiChevronRight,
  FiBell,
  FiShield,
  FiHelpCircle
} from "react-icons/fi";
import {
  MdCases,
  MdGavel,
  MdPerson,
  MdCalendarToday
} from "react-icons/md";

import { FiCreditCard } from "react-icons/fi";

import SolicitorAppointments from "./SolicitorAppointments";
import Calendar from "./Calendar";
import SolicitorPracticeArea from "./SolicitorPracticeArea";
import SolicitorPlanner from "./SolicitorPlanner";
import SolicitorProfile from "./SolicitorProfile";
import SolicitorCases from "./SolicitorCases";
import SolicitorSettings from "./SolicitorSettings";
import SolicitorAgreementPolicy from "./SolicitorAgreementPolicy";
import SolicitorHelp from "./SolicitorHelp";
import Subscription from "./Subscription"
import SubscriptionCountdown from "../components/SubscriptionCountdown";

import "../styles/SolicitorDashboard.css";

export default function SolicitorDashboard() {
  const { logout, token } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(() => {
    return sessionStorage.getItem("solicitor_active_section") || "appointments";
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notificationCount, setNotificationCount] = useState(0);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!res.ok) throw new Error("Failed to load user");

        const data = await res.json();
        setUserData(data.user);

        // Fetch notifications for solicitor
        fetchNotifications();
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchNotifications = async () => {
    try {
      if (!token) return;

      const res = await fetch(
        `${API_BASE_URL}/api/appointments/solicitor/my`, // ✅ CORRECT ENDPOINT
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!res.ok) return;

      const data = await res.json();
      const appointments = data.appointments || [];

      // 🔔 ONLY SHOW WHEN:
      // status === "PENDING" AND appointmentCallStatus === "NOT_STARTED"

      const pendingMeetings = appointments.filter(app => {
        return (
          app.status === "PENDING" &&
          app.appointmentCallStatus === "NOT_STARTED"
        );
      });

      setNotificationCount(pendingMeetings.length);

    } catch (err) {
      console.error("Solicitor notification fetch failed:", err);
    }
  };



  useEffect(() => {
    if (!token) return;

    fetchNotifications();

    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);


  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleNavigation = (sectionId) => {
    setActiveSection(sectionId);
    sessionStorage.setItem("solicitor_active_section", sectionId);
    setSidebarOpen(false);
  };

  const menuItems = [
    {
      id: "appointments",
      label: "Client Consultations",
      icon: <FiCalendar />,
      badge: null
    },
    {
      id: "cases",
      label: "Case Docket",
      icon: <MdCases />,
      badge: null
    },
    {
      id: "planner",
      label: "Hearing Planner",
      icon: <MdGavel />,
      badge: null
    },
    {
      id: "calendar",
      label: "Schedules",
      icon: <MdCalendarToday />,
      badge: null
    },
    // {
    //   id: "chat",
    //   label: "Messages",
    //   icon: <FiMessageCircle />,   
    //   badge: null
    // },
    {
      id: "practice-areas",
      label: "Practice Areas",
      icon: <FiBriefcase />,
      badge: null
    },
    {
      id: "profile",
      label: "My Profile",
      icon: <MdPerson />,
      badge: null
    },
    {
      id: "settings",
      label: "Workspace Settings",
      icon: <FiSettings />,
      badge: null
    },
    {
      id: "agreements",
      label: "Agreements & Policies",
      icon: <FiFileText />,
      badge: null
    },
    {
      id: "help",
      label: "Help & Support",
      icon: <FiHelpCircle />,
      badge: null
    },
    {
      id: "subscription",
      label: "Subscription",
      icon: <FiCreditCard />,
      badge: null
    }
  ];

  if (loading) {
    return (
      <div className="legal-loading-container">
        <div className="legal-loading-spinner"></div>
        <p>Loading your professional dashboard...</p>
      </div>
    );
  }

  return (
    <div className="legal-professional-wrapper">
      {/* ======================
         MOBILE HEADER
      ====================== */}
      <header className="legal-mobile-header">
        <button
          className="legal-mobile-menu-btn"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <FiX size={20} /> : <FiMenu size={20} />}
        </button>

        <div className="legal-mobile-brand">
          <div className="legal-brand-icon-wrapper">
            <MdGavel className="legal-brand-icon" size={22} />
          </div>
          <span>Solicitor Hub</span>
        </div>
      </header>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="legal-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ======================
         LEGAL PROFESSIONAL SIDEBAR
      ====================== */}
      <aside className={`legal-professional-sidebar ${sidebarOpen ? "open" : ""}`}>
        {/* Brand Section */}
        <div className="legal-sidebar-brand">
          <div className="legal-brand-content">
            <div className="legal-brand-icon-wrapper">
              <MdGavel className="legal-brand-icon" size={28} />
            </div>
            <div>
              <h2>Solicitor Hub</h2>
              <p className="legal-brand-subtitle">Legal Dashboard</p>
            </div>
          </div>
        </div>

        {/* User Profile Card */}
        <div className="legal-professional-user-card">
          <div className="legal-user-avatar-container">
            {userData?.profile?.photo ? (
              <img
                src={userData.profile.photo}
                alt={userData.name}
                className="legal-user-avatar"
              />
            ) : (
              <div className="legal-avatar-fallback">
                {userData?.name?.charAt(0)?.toUpperCase() || "S"}
              </div>
            )}
            <div className="legal-user-status-indicator online"></div>
          </div>

          <div className="legal-user-info">
            <h3 className="legal-user-name">
              {userData?.name || "Solicitor"}
            </h3>
            <p className="legal-user-email">
              {userData?.email || "solicitor@example.com"}
            </p>

            <div className="legal-user-role-tag">
              <FiShield size={12} />
              <span>Legal Practitioner</span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="legal-sidebar-nav">
          <div className="legal-nav-header">
            <span className="legal-nav-title">NAVIGATION</span>
          </div>

          <ul className="legal-nav-menu">
            {menuItems.map(item => (
              <li
                key={item.id}
                className={`legal-nav-item ${activeSection === item.id ? "active" : ""
                  }`}
                onClick={() => handleNavigation(item.id)}
              >
                <div className="legal-nav-item-content">
                  <div className="legal-nav-icon-wrapper">
                    {item.icon}
                  </div>
                  <div className="legal-nav-text">
                    <span className="legal-nav-label">{item.label}</span>
                    {item.badge && (
                      <span className="legal-nav-badge">{item.badge}</span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </nav>

        {/* Sidebar Footer */}
        <div className="legal-sidebar-footer">
          <div className="legal-footer-content">

            <div className="legal-footer-actions">
              <button
                className="legal-logout-btn"
                onClick={handleLogout}
              >
                <FiLogOut size={16} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* ======================
         MAIN CONTENT (WHITE MODE)
      ====================== */}
      <main className="legal-dashboard-main">
        <div className="legal-content-wrapper">

          {/* ======================
             BREADCRUMB & ACTIONS
          ====================== */}
          <div className="legal-breadcrumb-wrapper">
            {/* Left - Breadcrumb */}
            <div className="legal-breadcrumb">
              <span className="legal-breadcrumb-item">Solicitor Portal</span>
              <FiChevronRight size={12} />
              <span className="legal-breadcrumb-item active">
                {menuItems.find(item => item.id === activeSection)?.label || "Consultations"}
              </span>
            </div>

            {/* Center MFA Toggle */}
            {/* <div className="legal-header-center">
              <MfaToggle user={userData} />
            </div> */}

            <div className="legal-header-center">
              <SubscriptionCountdown />
            </div>

            <div
              className="legal-notification-wrapper"
              onClick={() => {
                setActiveSection("appointments");
                sessionStorage.setItem("solicitor_active_section", "appointments");
              }}
              title="Appointment Notifications"
            >
              <FiBell size={20} />
              {notificationCount > 0 && (
                <span className="legal-notification-badge">
                  {notificationCount}
                </span>
              )}
            </div>



          </div>

          {/* ======================
             DYNAMIC CONTENT
          ====================== */}
          <div className="legal-content-container">
            {activeSection === "appointments" && <SolicitorAppointments />}
            {activeSection === "cases" && <SolicitorCases />}
            {activeSection === "calendar" && <Calendar />}
            {activeSection === "practice-areas" && <SolicitorPracticeArea />}
            {activeSection === "profile" && <SolicitorProfile />}
            {activeSection === "settings" && <SolicitorSettings />}
            {activeSection === "planner" && <SolicitorPlanner />}
            {activeSection === "agreements" && <SolicitorAgreementPolicy />}
            {activeSection === "help" && <SolicitorHelp />}
            {activeSection === "subscription" && <Subscription />}
          </div>
        </div>
      </main>
    </div>
  );
}